// LLM generated - Model Claude 4 Sonnet
import { useCallback, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { storeLastReadMessage } from '../live-activity-service.service.api';
import type { GetUnreadMessagesResponse, StoreLastReadMessagePayload } from '../types';
import { logError } from '~/common/utils/log';
import { createQueryKey } from '~/domains/contest/domains/gamecenter/domains/common/components/ContestChat/constants/queryKeys';

interface UseLastReadMessageOptions {
  /** The chat ID to track - hook is disabled if undefined */
  chatId?: string;
}

/** Debounce delay - wait this long after the last message before sending */
const DEBOUNCE_DELAY = 1000;

/**
 * Hook for marking messages as read with debouncing and deduplication.
 *
 * Key behaviors:
 * - Debounces rapid calls (e.g., multiple Pusher messages arriving quickly)
 * - Tracks last sent messageId per chatId to prevent duplicate API calls
 * - Optimistically updates unread count to 0 when marking as read
 * - Caller is responsible for deciding WHEN to call markAsRead
 */
export const useLastReadMessage = ({ chatId }: UseLastReadMessageOptions) => {
  const queryClient = useQueryClient();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingMessageIdRef = useRef<string | null>(null);

  // Track last sent message ID per chatId - survives tab switches
  const lastSentByChatRef = useRef<Map<string, string>>(new Map());

  const mutation = useMutation({
    mutationFn: (payload: StoreLastReadMessagePayload) => storeLastReadMessage(payload),
    onError: (error) => {
      logError('Failed to store last read message:', error);
    },
  });

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * Mark a message as read (debounced).
   * Only sends API call if messageId is different from the last one sent for this chatId.
   */
  const markAsRead = useCallback(
    (messageId: string) => {
      if (!chatId) return;

      // Check if we've already sent this exact messageId for this chatId
      const lastSentForChat = lastSentByChatRef.current.get(chatId);
      if (lastSentForChat === messageId) {
        return; // Already sent, skip
      }

      // Update pending to the latest message
      pendingMessageIdRef.current = messageId;

      // Reset debounce timer
      clearPendingTimeout();

      timeoutRef.current = setTimeout(() => {
        const pendingId = pendingMessageIdRef.current;
        if (!pendingId || !chatId) return;

        // Double-check we haven't sent this already (could have changed during debounce)
        if (lastSentByChatRef.current.get(chatId) === pendingId) {
          return;
        }

        // Optimistically set unread count to 0 for this chat
        queryClient.setQueryData<GetUnreadMessagesResponse>(
          createQueryKey.unreadMessages(chatId),
          (oldData) => {
            if (!oldData) return { id: chatId, data: { count: 0 } };
            return { ...oldData, data: { count: 0 } };
          }
        );

        mutation.mutate({
          chatId,
          messageId: pendingId,
        });

        // Track that we sent this messageId for this chatId
        lastSentByChatRef.current.set(chatId, pendingId);
        pendingMessageIdRef.current = null;
      }, DEBOUNCE_DELAY);
    },
    [chatId, clearPendingTimeout, mutation, queryClient]
  );

  const hasMarkedAsRead = useCallback(
    (messageId: string): boolean => {
      if (!chatId) return false;
      return lastSentByChatRef.current.get(chatId) === messageId;
    },
    [chatId]
  );

  const getLastSentMessageId = useCallback((): string | undefined => {
    if (!chatId) return undefined;
    return lastSentByChatRef.current.get(chatId);
  }, [chatId]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      clearPendingTimeout();
    },
    [clearPendingTimeout]
  );

  return {
    markAsRead,
    hasMarkedAsRead,
    getLastSentMessageId,
    isLoading: mutation.isLoading,
  };
};
